<!-- TEMPLATE -->
<template>
    <div :class='userDetailsClass'>
        <dean-two-column-layout :title="headerTitle">
            <dean-section-title-remove-restore
                v-if="canRemoveUser()"
                slot="title-items"
                :is-removed="isDeleted"
                text-remove="Verwijderen" :remove-icon="trashIcon" @remove="deleteUser"
                text-restore="Herstellen" :restore-icon="lockOpenIcon" @restore="restoreUser"
            >
            </dean-section-title-remove-restore>
            <dean-section-title-item
                v-if="canSaveUser()"
                :icon='saveIcon'
                slot="title-items"
                :content="true"
                :large="true"
                @click.native="processUser"
            >
                Opslaan
            </dean-section-title-item>
            <div class="column" slot="left">
                <dean-form-row id="givenName" v-model="userData.personalName.givenName"
                               label="Voornaam"
                               @input="inputFieldChanged"
                               :required-field="true"
                               :disabled='!canSaveUser()'
                               :warning="$v.data.personalName.givenName.$error"></dean-form-row>
                <dean-form-row id="surnamePrefix" v-model="userData.personalName.surnamePrefix"
                               label="Tussenvoegsels"
                               :disabled='!canSaveUser()'
                               @input="inputFieldChanged"></dean-form-row>
                <dean-form-row id="surname" v-model="userData.personalName.surname" label="Achternaam"
                               @input="inputFieldChanged"
                               :required-field="true"
                               :disabled='!canSaveUser()'
                               :warning="$v.data.personalName.surname.$error"
                               @focus="$v.data.personalName.surname.$touch()"></dean-form-row>
                <dean-form-row id="jobTitle" v-model="userData.personalName.jobTitle" label="Functietitel"
                               :disabled='!canSaveUser()'
                               @input="inputFieldChanged"></dean-form-row>
                <dean-form-row id="phoneNumber" v-model="data.phoneNumber" label="Telefoonnummer"
                               :disabled='!canSaveUser()'
                               @input="inputFieldChanged"></dean-form-row>
            </div>
            <div class="column" slot="right">
                <dean-form-row id="emailAddress" v-model="data.emailAddress" label="E-mailadres"
                               @input="inputFieldChanged"
                               :disabled='disableEmailAddressField || !canSaveUser()'
                               :required-field="true" :warning="$v.data.emailAddress.$error"
                               @focus="$v.data.emailAddress.$touch()"></dean-form-row>
                <dean-multi-select-form-row :multiple="true" placeholder="Kies rollen"
                                            :required-field="true"
                                            :disabled='!canSaveUser()'
                                            :warning="$v.data.roles.$error"
                                            id='generic.Roles' label='Rollen' :value="selectedRoles"
                                            :options='userAvailableRolesList'
                                            @select='addRole'
                                            @remove="removeRole"></dean-multi-select-form-row>
                <dean-input-switch-form-row id="receivesInvoices" :value="userData.receivesInvoices"
                                            :disabled='!canSaveUser()'
                                            @checked="inputSwitchChanged"
                                            label="Ontvangt facturen"></dean-input-switch-form-row>
                <dean-input-switch-form-row id="receivesNotifications" :value="userData.receivesNotifications"
                                            :disabled='!canSaveUser()'
                                            @checked="inputSwitchChanged"
                                            label="Ontvangt berichten"></dean-input-switch-form-row>
            </div>
        </dean-two-column-layout>
    </div>
</template>

<!-- SCRIPT -->

<script lang='ts'>
  // VUE
  import {Component, Vue, Watch} from 'vue-property-decorator';
  import vSelect from 'vue-select';
  import axios from 'axios';
  import {deanDialogBus} from '@/main';
  import {validationMixin} from 'vuelidate';
  import {required, email} from 'vuelidate/lib/validators';
  import {Validations} from 'vuelidate-property-decorators';
  import {createNotification} from '@/mixins/validations';
  import {getErrorFromResponse} from '@/mixins/errorMessage';
  import {CollapseTransition} from 'vue2-transitions';

  import {
    customerIcon,
    businessIcon,
    commissionIcon,
    partnerIcon,
    deanAlertDialog,
    deanIcon,
    deanInputField,
    deanSearchField,
    deanSectionDivider,
    deanSectionTitle,
    deanSectionTitleItem,
    invoicesIcon,
    messageIcon,
    offersIcon,
    ordersIcon,
    returnIcon,
    saveIcon,
    summeryIcon,
    usersIcon,
    lockOpenIcon,
    trashIcon,
    warningIcon,
  } from '@deanone/dean-components';
  import DeanTwoColumnLayout from '@/components/deanComponents/deanTwoColumnLayout.vue';
  import deanInputSelect from '@/components/deanComponents/deanInputSelect.vue';
  import DeanSelectFormRow from '@/components/deanComponents/deanSelectFormRow.vue';
  import DeanInputSwitchFormRow from '@/components/deanComponents/deanInputSwitchFormRow.vue';
  import DeanMultiSelectFormRow from '@/components/deanComponents/deanMultiSelectFormRow.vue';
  import deanSectionTitleRemoveRestore from '@/components/deanComponents/deanSectionTitleRemoveRestore.vue';
  import deanFormRow from '@/components/deanComponents/deanFormRow.vue';
  import sideMenuPartnerMixin from '@/mixins/sideMenuPartnerMixin';
  import {mixins} from 'vue-class-component';
  import {authUserHasPrivilegeAndAction} from "@/mixins/privileges";
  import {UserPrivilege} from "@/enums/UserPrivilege";
  import {UserAction} from "@/enums/UserAction";

  @Component({
    components: {
      deanSectionTitleRemoveRestore,
      DeanMultiSelectFormRow,
      DeanInputSwitchFormRow,
      DeanSelectFormRow,
      deanAlertDialog,
      deanInputField,
      DeanTwoColumnLayout,
      deanInputSelect,
      deanFormRow,
      deanSectionDivider,
      deanSectionTitle,
      deanSectionTitleItem,
      deanSearchField,
      vSelect,
      invoicesIcon,
      deanIcon,
      returnIcon,
      saveIcon,
      summeryIcon,
      partnerIcon,
      businessIcon,
      usersIcon,
      offersIcon,
      ordersIcon,
      commissionIcon,
      messageIcon,
      CollapseTransition,
    },
    mixins: [validationMixin],
  })

  export default class Details extends mixins(sideMenuPartnerMixin) {
    public saveIcon: object = saveIcon;
    public lockOpenIcon: object = lockOpenIcon;
    public trashIcon: object = trashIcon;
    public showSomething: boolean = false;
    public timeout?: number;
    public roleOptions: object[] = [];
    public data: {
      id: any,
      organizationId: any,
      personalName: {
        givenName: any,
        additionalName: any,
        surnamePrefix: any,
        surname: any,
        jobTitle: any,
        nameStyle: any,
      },
      emailAddress: any,
      phoneNumber: any,
      isUser: boolean,
      roles: any,
      receivesInvoices: any,
      receivesNotifications: any,
    } = {
      id: null,
      organizationId: null,
      personalName: {
        givenName: null,
        additionalName: null,
        surnamePrefix: null,
        surname: null,
        jobTitle: null,
        nameStyle: null,
      },
      emailAddress: null,
      phoneNumber: null,
      isUser: true,
      roles: [],
      receivesInvoices: false,
      receivesNotifications: false,
    };
    public state: string = '';
    public isDeleted: boolean = false;
    public availableRoles: any[] = [];
    public userEmailAddress: string = '';

    // COMPUTED
    @Validations()
    get validations() {
      return {
        data: {
          personalName: {
            surname: {required},
            givenName: {required},
          },
          emailAddress: {required, email},
          roles: {required},
        },
      };
    }

    get userDetailsClass() {
      let value = 'partner-add';
      if (this.showSomething) {
        value += ' no-scroll-container';
      }
      return value;
    }

    get userData() {
      return this.data;
    }

    get userAvailableRolesList() {
      return this.availableRoles;
    }

    get headerTitle(): string {
      return (this.$route.params.userId !== undefined) ? 'Gebruiker wijzigen' : 'Gebruiker toevoegen';
    }

    get disableEmailAddressField() {
      return this.$route.params.userId !== undefined;
    }

    public inputSwitchChanged(field: { id: any, value: any }) {
      const key: string = field.id;
      // @ts-ignore-next-line
      this.data[key] = field.value;
    }

    public inputFieldChanged(field: any) {
      const name: string = field.id;
      // @ts-ignore-next-line
      this.data[name] = field.value;
    }

    get selectedRoles() {
      this.updateAvailableRoleOptions();
      return this.data.roles;
    }

    public addRole(field: any) {
      this.data.roles.push(field);
      this.updateAvailableRoleOptions();
    }

    public removeRole(field: any) {
      this.data.roles = this.data.roles.filter((role: any) => {
        if (role.value !== field.value) {
          return role;
        }
      });
      this.updateAvailableRoleOptions();
    }

    public updateAvailableRoleOptions() {
      if (this.data.roles.length <= 0) {
        this.availableRoles = this.roleOptions;
        return;
      }

      const selectedRoles: any[] = [];

      this.data.roles.forEach((role: any) => {
        selectedRoles[role.value] = role.label;
      });

      this.availableRoles = this.roleOptions.filter((role: any) => {
        if (selectedRoles[role.value] === undefined) {
          return role;
        }
      });
    }

    public userDetails(): Promise<boolean> {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('setLoadOverlay', true);

        axios.post('/-/trade/api/person.roles.list', {
            partnerId: this.$route.params.pid,
          }).then(
            (response) => {
              this.roleOptions = response.data;
            },
            (error) => {
              this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
            },
          )
          .finally(
            () => {
              if (this.$route.params.userId) {
                this.$store.dispatch('setLoadOverlay', true);
                axios.post('/-/trade/api/partner.contact.info', {
                  contactId: this.$route.params.userId,
                }).then(
                  (response) => {
                    this.isDeleted = Boolean(response.data.deleted);

                    if (this.$route.params.userId != null) {
                      this.data = {...this.data, ...response.data};

                      this.data.roles = response.data.roles.map((role: any) => {
                        return {
                          value: role.value,
                          label: role.label,
                        };
                      });
                    }
                    this.userEmailAddress = response.data.userEmailAddress;
                  },
                  (error) => {
                    this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
                  },
                ).finally(
                  () => {
                    this.$store.dispatch('setLoadOverlay', false);
                  },
                );
              } else {
                this.$store.dispatch('setLoadOverlay', false);
              }
            },
          );

      });
    }

    public created() {
      this.userDetails();
      this.buildSideMenuPartner();
    }

    public deleteUser() {
      deanDialogBus.$emit('open', 'DeleteUserDialog', {
        id: this.$route.params.userId,
        context: 'partners',
        contextId: this.$route.params.pid,
        givenName: this.data.personalName.givenName,
        surnamePrefix: this.data.personalName.surnamePrefix,
        surname: this.data.personalName.surname,
      });
    }

    public restoreUser() {
      deanDialogBus.$emit('open', 'DeleteUserDialog', {
        id: this.$route.params.userId,
        context: 'partners',
        contextId: this.$route.params.pid,
        givenName: this.data.personalName.givenName,
        surnamePrefix: this.data.personalName.surnamePrefix,
        surname: this.data.personalName.surname,
        state: 'restoring',
      });
    }

    public processUser() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.$store.dispatch('setLoadOverlay', true);

        const url = '/-/trade/api/partner.contact.save';

        this.data.organizationId = this.$route.params.pid;
        this.data.isUser = true;

        axios
          .post(url, this.userData).then(
          (response) => {
            this.$toast({
              icon: saveIcon,
              text: 'Gebruiker opgeslagen',
              theme: 'success',
            });
            this.$store.dispatch('setLoadOverlay', false);

            this.$router.push({
              name: 'partners.users.list',
              params: {
                pid: this.$route.params.pid,
              },
            });
          },
          (error) => {
            const errorMessage: string|null = getErrorFromResponse(
              error.response.data,
              this.$store.state.main.notification.messages.couldNotSendData.text,
            );

            this.$toast({
              icon: warningIcon,
              text: errorMessage ?? '',
              theme: 'danger',
            });
            this.$store.dispatch('setLoadOverlay', false);
          },
        );
      } else {
        this.$toast(createNotification(this.$v));
      }
    }

    public canSaveUser(): boolean {
      if (this.isDeleted) {
        return false;
      }

      if (
          this.data?.id
          && authUserHasPrivilegeAndAction(UserPrivilege.USER, UserAction.UPDATE)
      ) {
        return true;
      }

      if (
          this.data?.id === null
          && authUserHasPrivilegeAndAction(UserPrivilege.USER, UserAction.CREATE)
      ) {
        return true;
      }

      return false;
    }

    public canRemoveUser(): boolean {
      if (
          this.$route.name === 'partner.user.edit'
          && authUserHasPrivilegeAndAction(UserPrivilege.USER, UserAction.CHANGE_STATUS)
      ) {
        return true;
      }

      return false;
    }

    public mounted() {
      deanDialogBus.$on('userStatusChange', () => {
        this.userDetails();
      });
    }

    public destroyed() {
      deanDialogBus.$off('userStatusChange');
    }
  }
</script>

<!-- STYLE -->

<style lang='scss' scoped>
    @import '~@deanone/dean-components/src/scss/vars';
</style>
